import { useParams } from "@/routes/hooks/use-params";
import { useRouter } from "@/routes/hooks/use-router";
import { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "@/hooks/use-auth-context";
import {
  useGetUserWorkspace,
  useGetUserWorkspaces,
} from "@/api/workspace/workspace";

import { WorkspaceContext } from "./workspace-context";

type Props = {
  children: React.ReactNode;
};

export function WorkspaceProvider({ children }: Props) {
  const { user, authenticated, loading: authLoading } = useAuthContext();
  const [workspaceError, setWorkspaceError] = useState<Error | null>(null);
  const router = useRouter();
  const params = useParams();

  // Get workspace ID from URL params if available
  const urlWorkspaceId = params.workspaceId || null;
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(
    urlWorkspaceId
  );

  // Fetch all workspaces the user has access to
  const {
    workspaces: availableWorkspaces,
    workspacesLoading,
    workspacesRefresh,
  } = useGetUserWorkspaces(user?.id);

  // Fetch current workspace data
  const {
    workspace,
    workspaceLoading,
    workspaceError: fetchError,
    workspaceRefresh,
  } = useGetUserWorkspace(user?.id);

  // Function to switch to a different workspace
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      const targetWorkspace = availableWorkspaces.find(
        (ws) => ws.id === workspaceId
      );

      if (!targetWorkspace) {
        console.error(`Workspace with ID ${workspaceId} not found`);
        return;
      }

      // Force refresh both workspace data sources before updating state and URL
      // This ensures fresh data is fetched for the new workspace
      await workspaceRefresh();
      await workspacesRefresh();

      // Save workspace ID to localStorage for persistence across refreshes
      localStorage.setItem("lastWorkspace", workspaceId);

      setCurrentWorkspaceId(workspaceId);

      // Update URL to reflect the workspace change
      // Check if we're already in a workspace route
      const currentPath = window.location.pathname;

      if (currentPath.includes("/dashboard/")) {
        // Replace the current workspace ID in the URL
        const newPath = currentPath.replace(
          /\/dashboard\/([^/]*)/,
          `/dashboard/${workspaceId}`
        );

        // Use push instead of replace to ensure a full navigation
        // This makes sure all components re-render with new workspace context
        router.push(newPath);
      } else {
        // Navigate to the dashboard root with the workspace ID
        router.push(`/dashboard/${workspaceId}`);
      }
    },
    [availableWorkspaces, router, workspaceRefresh, workspacesRefresh]
  );

  // Set the initial workspace if none is selected but workspaces are available
  useEffect(() => {
    if (
      authenticated &&
      !authLoading &&
      !workspacesLoading &&
      availableWorkspaces.length > 0
    ) {
      // First check if we have a URL workspace ID - that takes precedence
      if (urlWorkspaceId) {
        // If URL has workspace ID but it's not set as current, update it
        if (urlWorkspaceId !== currentWorkspaceId) {
          setCurrentWorkspaceId(urlWorkspaceId);
        }
        return;
      }

      // If no URL workspace ID but we have currentWorkspaceId, keep using it
      if (currentWorkspaceId) {
        // Verify the current workspace ID is valid
        const workspaceExists = availableWorkspaces.some(
          (ws) => ws.id === currentWorkspaceId
        );

        if (workspaceExists) {
          return;
        }
      }

      // If no valid workspace ID from URL or state, try localStorage
      const savedWorkspaceId = localStorage.getItem("lastWorkspace");
      if (savedWorkspaceId) {
        // Verify the saved workspace ID is valid
        const workspaceExists = availableWorkspaces.some(
          (ws) => ws.id === savedWorkspaceId
        );

        if (workspaceExists) {
          setCurrentWorkspaceId(savedWorkspaceId);
          return;
        }
      }

      // If no workspace ID from any source, use default logic
      // Find user's primary workspace (first owned workspace) or fallback to first available
      const ownedWorkspace = availableWorkspaces.find(
        (ws) => ws.ownerId === user?.id
      );
      const firstWorkspace = availableWorkspaces[0];

      setCurrentWorkspaceId(ownedWorkspace?.id || firstWorkspace?.id || null);
    }
  }, [
    authenticated,
    authLoading,
    workspacesLoading,
    availableWorkspaces,
    currentWorkspaceId,
    urlWorkspaceId,
    user?.id,
  ]);

  // Update current workspace when URL changes
  useEffect(() => {
    if (urlWorkspaceId && urlWorkspaceId !== currentWorkspaceId) {
      // Save workspace ID from URL to localStorage
      localStorage.setItem("lastWorkspace", urlWorkspaceId);

      setCurrentWorkspaceId(urlWorkspaceId);

      // Force refresh workspace data when URL workspace ID changes
      // This ensures data is consistent with the URL
      const refreshData = async () => {
        await Promise.all([workspaceRefresh(), workspacesRefresh()]);
      };

      refreshData();
    }
  }, [urlWorkspaceId, currentWorkspaceId, workspaceRefresh, workspacesRefresh]);

  // Set error if there is one
  useEffect(() => {
    if (fetchError) {
      setWorkspaceError(fetchError);
    }
  }, [fetchError]);

  // Get the active workspace from the list of available workspaces
  // Use URL workspace ID as the source of truth if available
  const activeWorkspaceId = urlWorkspaceId || currentWorkspaceId;

  const activeWorkspace = activeWorkspaceId
    ? availableWorkspaces.find((ws) => ws.id === activeWorkspaceId) || null
    : authenticated && workspace.length > 0
    ? workspace[0]
    : null;

  return (
    <WorkspaceContext.Provider
      value={{
        workspace: activeWorkspace,
        workspaceLoading:
          authLoading ||
          (authenticated && (workspaceLoading || workspacesLoading)),
        workspaceError,
        availableWorkspaces,
        availableWorkspacesLoading: workspacesLoading,
        switchWorkspace,
        workspaceRefresh: async () => {
          await Promise.all([workspaceRefresh(), workspacesRefresh()]);
        },
        currentWorkspaceId,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
